import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { useAppSelector } from '@hooks/redux.hooks';
import useListFormatter from '@hooks/useListFormatter';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectActiveOrganization } from '@redux/authent/authent.selectors';
import { selectGroupedPolygonWithSelectedPolygon, selectPolygonById } from '@redux/situation/situation.selectors';
import { formatDate } from '@utils/date.utils';
import { getPolygonIcon } from '@utils/map/polygonDetection.utils';
import { getTargetDisplayedId, getTargetIdentification } from '@utils/target.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import { Target } from '@/types/c2/c2.types';
import { DateTimeEnum } from '@/types/dateTime.types';
import { PolygonInfoData, PopupPositionData } from '@/types/popup.types';
import { PolygonLocation } from '@/types/sensor/sensor.types';

import InfoValue from './InfoValue';

function PolygonInfo({
  mapConstraintsRef,
  targetId,
  top,
  left,
  onClose,
}: Readonly<PolygonInfoData & PopupPositionData>) {
  const { formatMessage } = useIntl();
  const formatList = useListFormatter();

  const target: Target<PolygonLocation> | undefined = useSelectorWithReplayMode(selectPolygonById, targetId);
  const groupedTarget = useSelectorWithReplayMode(selectGroupedPolygonWithSelectedPolygon, target);
  const organization = useAppSelector(selectActiveOrganization);

  if (!target) {
    onClose();
    return null;
  }

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left} offsetY={-133}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="polygonInfos.polygonInfos" />
      </DraggablePanel.Header>
      <DraggablePanel.Body height={520}>
        <HStack alignItems="start" backgroundColor="neutral.700" width="442" minHeight="100%" gap={0.25}>
          <VStack alignItems="start" flexGrow={1} gap={4} marginLeft={4} marginTop={4}>
            <InfoValue label={formatMessage({ id: 'polygonInfos.targetId' })} value={getTargetDisplayedId(target)} />
            <InfoValue
              label={formatMessage({ id: 'polygonInfos.type' })}
              value={formatMessage({ id: `target.identification.${getTargetIdentification(target, organization)}` })}
            />
            <InfoValue
              label={formatMessage({ id: 'polygonInfos.detector' })}
              value={formatList(target.trackIds.map((track) => track.sensorCode))}
            />
            <InfoValue
              label={formatMessage({ id: 'polygonInfos.droneType' })}
              value={target.collaborativeData?.model ?? 'N/A'}
            />
            <VStack alignItems="start">
              <Text textStyle="md" fontWeight="medium" color="neutral.300">
                {formatMessage({ id: 'polygonInfos.detection' })}
              </Text>
              <HStack width="100%" gap={2} alignItems="start">
                <VStack alignItems="start" justifyContent="end">
                  <HStack width="100%" gap={1} alignItems="start">
                    <Text textStyle="md" fontWeight="medium" width="65px" color="neutral.300" lineClamp={1}>
                      {formatMessage({ id: 'polygonInfos.start' })}
                    </Text>
                    <Text textStyle="lg" fontWeight="medium">
                      {formatDate(target.detectionStart, DateTimeEnum.DATE_TIME)}
                    </Text>
                  </HStack>
                  <HStack width="100%" gap={1} alignItems="start">
                    <Text textStyle="md" fontWeight="medium" width="65px" color="neutral.300" lineClamp={1}>
                      {formatMessage({ id: 'polygonInfos.end' })}
                    </Text>
                    <Text textStyle="lg" fontWeight="medium">
                      {formatDate(target.detectionEnd, DateTimeEnum.DATE_TIME)}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>
            </VStack>
            <InfoValue
              label={formatMessage({ id: 'polygonInfos.linkedTo' })}
              value={getTargetDisplayedId(groupedTarget)}
            />
          </VStack>
          <Icon as={getPolygonIcon(target, organization)} width="70px" height="70px" margin={4} />
        </HStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}

export default PolygonInfo;
